import Lenis from 'lenis';
import { getScrollContainer, scrollContainerMediaQuery, setSmoothScroller } from '@theme/scroll-container';

/**
 * Scroll glide duration in seconds, paired with Lenis's default expo-out
 * easing: a quick start and a long, slow settle — the signature smooth feel.
 * @constant {number}
 */
const SMOOTH_DURATION = 1.2;

let lenis = null;
let contentObserver = null;

const scrollerAdapter = {
  scrollTo(options) {
    if (!lenis || typeof options.top !== 'number') return;

    lenis.scrollTo(options.top, options.behavior === 'instant' ? { immediate: true } : {});
  },
};

function initSmoothScrolling() {
  if (lenis) return;

  const container = getScrollContainer();
  if (!(container instanceof HTMLElement)) return;

  // Narrow viewports scroll the document itself: `documentElement` as wrapper
  // is auto-converted to `window` by Lenis, and `document.body` as content
  // keeps the scroll limit in sync with content growth. Touch input stays
  // fully native (`syncTouch` is false), so phones/tablets are unaffected.
  const isDocumentScroll = container === document.documentElement;

  lenis = new Lenis({
    wrapper: container,
    content: isDocumentScroll ? document.body : container,
    duration: SMOOTH_DURATION,
    anchors: true,
    allowNestedScroll: true,
    stopInertiaOnNavigate: true,
    autoRaf: true,
  });

  if (!isDocumentScroll) {
    // `.page-wrapper` has no single content child, so observe each child and
    // refresh the cached scroll limit when content grows (accordions, lazy media).
    // `dimensions.resize()` only refreshes cached measurements — safe mid-animation.
    contentObserver = new ResizeObserver(() => lenis?.dimensions.resize());
    for (const child of container.children) {
      contentObserver.observe(child);
    }
  }

  setSmoothScroller(scrollerAdapter);
}

function destroySmoothScrolling() {
  if (!lenis) return;

  setSmoothScroller(null);
  contentObserver?.disconnect();
  contentObserver = null;
  lenis.destroy();
  lenis = null;
}

initSmoothScrolling();

scrollContainerMediaQuery.addEventListener('change', () => {
  destroySmoothScrolling();
  initSmoothScrolling();
});
